/**
 * Global Search Hook
 * Custom hook for managing global search state with debouncing and geolocation
 */

import { useState, useEffect, useCallback } from 'react'
import { searchApi } from '../../../api/endpoints/search'
import { useGeolocation } from '../../../hooks/useGeolocation'
import type {
  GlobalSearchRequest,
  GlobalSearchResponse,
  SearchEntityType,
} from '../../../api/types/search'

interface UseGlobalSearchOptions {
  query: string
  entityType?: SearchEntityType
  tagIds?: string[]
  radiusKm?: number
  skip?: number
  take?: number
  sortBy?: string
  sortDescending?: boolean
  debounceMs?: number
  enabled?: boolean
}

interface UseGlobalSearchReturn {
  data: GlobalSearchResponse | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

const DEFAULT_RADIUS_KM = 50
const DEFAULT_DEBOUNCE_MS = 300
const DEFAULT_PAGE_SIZE = 20

/**
 * Hook for performing global search across Adventures, Users, and Groups
 * Automatically includes user's current location and debounces search queries
 */
export function useGlobalSearch(options: UseGlobalSearchOptions): UseGlobalSearchReturn {
  const {
    query,
    entityType,
    tagIds,
    radiusKm = DEFAULT_RADIUS_KM,
    skip = 0,
    take = DEFAULT_PAGE_SIZE,
    sortBy,
    sortDescending,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    enabled = true,
  } = options

  const [data, setData] = useState<GlobalSearchResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [debouncedQuery, setDebouncedQuery] = useState(query)

  // Get user's current location
  const { location } = useGeolocation()

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [query, debounceMs])

  // Perform the search
  const performSearch = useCallback(async () => {
    // Don't search if disabled or query is empty
    if (!enabled || !debouncedQuery.trim()) {
      setData(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const searchRequest: GlobalSearchRequest = {
        query: debouncedQuery.trim(),
        type: entityType,
        tagIds: tagIds?.length ? tagIds : undefined,
        latitude: location?.latitude,
        longitude: location?.longitude,
        radiusKm,
        skip,
        take,
        sortBy,
        sortDescending,
      }

      const response = await searchApi.search(searchRequest)
      setData(response)
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Search failed')
      setError(error)
      console.error('Global search error:', error)
    } finally {
      setIsLoading(false)
    }
  }, [
    enabled,
    debouncedQuery,
    entityType,
    tagIds,
    location?.latitude,
    location?.longitude,
    radiusKm,
    skip,
    take,
    sortBy,
    sortDescending,
  ])

  // Trigger search when debounced query or other parameters change
  useEffect(() => {
    performSearch()
  }, [performSearch])

  return {
    data,
    isLoading,
    error,
    refetch: performSearch,
  }
}
